import { Link } from './DiseaseClassification.styles';
import React, { useEffect, useState } from 'react';
import IWizardStep from '../../../../interfaces/IWizardStep';
import WizardButton from '../../components/WizardButton';
import { WizardContent } from '../../components/WizardContent';
import { WizardSecodaryTitle } from '../../components/WizardSecodaryTitle';
import { WizardTitle } from '../../components/WizardTitle';
import IDiseaseClassification from '../../../../interfaces/IDiseaseClassification';
import { Autocomplete, CircularProgress } from '@mui/material';
import WizardTextField from '../../components/WizardTextField';
import { useWizardRequest } from '../../../../hooks/services/useWizardRequest';
import { useToastrVanila } from '../../../../hooks/util/useToastrVanila';
import { useValidateSchema } from '../../../../hooks/providers/useValidateSchema';
import * as yup from 'yup';
import IFormError from '../../../../interfaces/IFormError';
import { debounce } from 'lodash';
import ITypeDocument from '../../../../interfaces/ITypeDocument';
import { useLocalStorage } from '../../../../hooks/providers/useLocalStorage';
import { ShowError, StyledAlert } from '../../components/ErrorMessage';

interface DiseaseClassificationProps extends IWizardStep {}

const DiseaseClassification = (props: DiseaseClassificationProps) => {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<readonly IDiseaseClassification[]>([]);
  const loadingInput = open;

  const [disease, setDisease] = useState<IDiseaseClassification | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  const wizardRequest = useWizardRequest();
  const toastr = useToastrVanila();
  const validation = useValidateSchema();
  const [errors, setErrors] = useState<IFormError | null>();
  const [documentType, setDocumentType] = useState<ITypeDocument | null>(null);
  const [cidMessage, setCidMessage] = useState<string | null>(null);
  const localStorage = useLocalStorage();

  useEffect(() => {
    let active = true;

    if (!loadingInput) {
      return undefined;
    }

    (async () => {
      if (active) {
        await loadDiseases('');
      }
    })();

    return () => {
      active = false;
    };
  }, [loadingInput]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  const loadDisease = async () => {
    setLoading(true);

    await wizardRequest
      .listDiseaseClassificationByMedicalCertificateId(`${props.medicalCertificate.id}`)
      .then(result => {
        setLoading(false);
        if (result && result.length > 0) {
          const disease = result[0];
          setDisease({ ...disease });
          setSavedId(disease.id || null);
        }
      })
      .catch(error => {
        setLoading(false);
        toastr.error(error.message);
      });
  };

  const updateDisease = async () => {
    setLoading(true);
    disease &&
      (await wizardRequest
        .updateDiseaseClassification(`${savedId}`, {
          ...disease,
          id: `${savedId}`,
          medicalCertificateId: props.medicalCertificate.id,
        })
        .then(async result => {
          setSavedId(result.id || null);
          await props
            .setStep(props.step + 1, { ...props.medicalCertificate, diseaseClassifications: [result] })
            .then(() => {
              setLoading(false);
            })
            .catch(error => {
              setLoading(false);
              toastr.error(error.message);
            });
        })
        .catch(error => {
          setLoading(false);
          toastr.error(error.message);
        }));
  };

  const createDisease = async () => {
    setLoading(true);
    disease &&
      (await wizardRequest
        .createDiseaseClassification({ ...disease, medicalCertificateId: props.medicalCertificate.id })
        .then(async result => {
          setSavedId(result.id || null);
          await props
            .setStep(props.step + 1, { ...props.medicalCertificate, diseaseClassifications: [result] })
            .then(() => {
              setLoading(false);
            })
            .catch(error => {
              toastr.error(error.message);
            });
        })
        .catch(error => {
          setLoading(false);
          toastr.error(error.message);
        }));
  };

  // Mensagem opcional configurada pela empresa. Falha aqui não interrompe o preenchimento.
  const loadCidMessage = async () => {
    await wizardRequest
      .getCidMessage()
      .then(result => {
        setCidMessage(result?.showCidMessage ? result.cidMessage : null);
      })
      .catch(() => {
        setCidMessage(null);
      });
  };

  useEffect(() => {
    const documentType = localStorage.getItem('documentType');
    setDocumentType({ ...documentType });
    loadDisease();
    loadCidMessage();
  }, []);

  const loadDiseases = debounce(async (filter = '') => {
    setLoading(true);
    await wizardRequest
      .listDiseaseClassification(filter)
      .then(diseaseClassification => {
        setOptions([...diseaseClassification, {}]);
        setLoading(false);
      })
      .catch(error => {
        toastr.error(`Erro ao carregar os dados: ${error.message}`);
        setLoading(false);
      });
  }, 500);

  const schema = yup.object({
    code: yup
      .string()
      .typeError("Por favor informe o código CID ou clique na opção 'Não tenho essa informação'.")
      .required('Campo obrigatório')
      .min(1, 'Campo obrigatório.'),
  });

  const handleSubmit = async () => {
    const result = await validation.validate(schema, { code: disease?.code || null });

    setErrors(null);

    if (!result.isValid) {
      return setErrors({ ...result.errors });
    }
    setLoading(true);
    if (savedId) {
      await updateDisease()
        .then(() => {
          setLoading(false);
        })
        .catch(error => {
          setLoading(false);
          toastr.error(error.message);
        });
    } else {
      await createDisease()
        .then(() => {
          setLoading(false);
        })
        .catch(error => {
          setLoading(false);
          toastr.error(error.message);
        });
    }
  };

  const advanceWhithoutData = async () => {
    await props
      .setStep(props.step + 1, { ...props.medicalCertificate, diseaseClassifications: [] })
      .then(() => {
        setLoading(false);
      })
      .catch(error => {
        setLoading(false);
        toastr.error(error.message);
      });
  };

  return (
    <WizardContent>
      <WizardTitle marginBotton={2}>Informe a CID do Atestado</WizardTitle>
      <WizardSecodaryTitle>(Código da Doença)</WizardSecodaryTitle>

      {cidMessage && cidMessage.trim() && (
        <div style={{ marginTop: '20px' }}>
          <ShowError show={true}>
            <StyledAlert severity="info">{cidMessage}</StyledAlert>
          </ShowError>
        </div>
      )}

      <form>
        <Autocomplete
          open={open}
          value={disease}
          onChange={(e: any, newValue) => {
            const castedDisease = { ...newValue } as IDiseaseClassification;
            setDisease({ ...castedDisease });
          }}
          onOpen={() => {
            setOpen(true);
            setDisease(null);
          }}
          onClose={() => {
            setOpen(false);
          }}
          isOptionEqualToValue={(option, value) => {
            return option?.description === value?.description;
          }}
          getOptionLabel={option => (option?.code ? `${option.code} - ${option.description}` : '')}
          options={options}
          loading={loading}
          renderInput={params => (
            <WizardTextField
              {...params}
              sx={{ marginTop: '30px', width: '300px' }}
              label="CID "
              error={Boolean(errors?.code)}
              helperText={errors?.code}
              onChange={e => {
                loadDiseases(e.target.value);
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
        {!documentType?.cidRequired ? (
          <Link onClick={advanceWhithoutData}>Não tenho essa informação</Link>
        ) : (
          <div style={{ marginTop: '100px' }} />
        )}

        <WizardButton onClick={handleSubmit} isLoading={loading} disabled={loading} sx={{ marginTop: '20px' }}>
          PRÓXIMO
        </WizardButton>
      </form>
    </WizardContent>
  );
};

export default DiseaseClassification;
